import {
  joinNumberingModel,
  previewNumber,
  splitNumberingModel,
  type Segment,
} from './document-numbering-model';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/hooks/useNotify';
import { api, ApiError } from '@/lib/api';
import { timezoneSelectOptions } from './timezoneOptions';

interface NumberingSettings {
  documentType: string;
  template: string;
  resetYearly: boolean;
  startNumber: number;
  nextNumber: number;
  periodCounters: Record<string, number>;
  timezone: string;
  enabled: boolean;
  revision: number;
  year: number;
}

function SequenceNumberField({
  value,
  width,
  onChange,
  label,
  helperText,
}: {
  value: string;
  width: number;
  onChange: (value: string) => void;
  label: string;
  helperText?: string;
}) {
  const [focused, setFocused] = useState(false);
  const displayValue = /^\d+$/.test(value) ? String(Number(value)).padStart(width, '0') : value;
  return (
    <TextField
      fullWidth
      label={label}
      helperText={helperText}
      value={focused ? value : displayValue}
      slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
      onFocus={() => {
        onChange(displayValue);
        setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        if (/^\d*$/.test(e.target.value)) onChange(e.target.value);
      }}
    />
  );
}

const queryKey = ['document-numbering'];
function NumberingEditor({ settings }: { settings: NumberingSettings }) {
  const { t } = useTranslation('settings');
  const client = useQueryClient();
  const notify = useNotify();
  const [segments, setSegments] = useState(() => splitNumberingModel(settings.template));
  const [nextNumber, setNextNumber] = useState(String(settings.nextNumber));
  const [startNumber, setStartNumber] = useState(String(settings.startNumber));
  const [resetYearly, setResetYearly] = useState(settings.resetYearly);
  const [timezone, setTimezone] = useState(settings.timezone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prefix =
    (
      { work_order: 'TM', invoice: 'INV', credit_note: 'HYV', receipt: 'KUI' } as Record<
        string,
        string
      >
    )[settings.documentType] ?? 'DOC';
  const template = joinNumberingModel(segments);
  const hasYear = segments.some((segment) => segment.type === 'year');
  const sequenceWidth = Number(segments.find((segment) => segment.type === 'sequence')?.value);
  const displayWidth =
    Number.isInteger(sequenceWidth) && sequenceWidth >= 1 && sequenceWidth <= 12
      ? sequenceWidth
      : 1;
  const year = Number(
    new Intl.DateTimeFormat('en', { year: 'numeric', timeZone: timezone }).format(new Date())
  );
  const previews = [0, 1, 2].map((offset) =>
    previewNumber(template, year, Number(nextNumber) + offset)
  );
  const nextYear = previewNumber(
    template,
    year + 1,
    resetYearly
      ? (settings.periodCounters[String(year + 1)] ?? Number(startNumber))
      : Number(nextNumber)
  );
  const valid =
    !!previewNumber(template, year, 999999999999) &&
    previews[0] !== null &&
    Number.isSafeInteger(Number(startNumber)) &&
    Number(startNumber) > 0 &&
    Number(startNumber) <= 999999999999 &&
    (!resetYearly || template.includes('{YYYY}'));
  const changeSegments = (next: Segment[]) => {
    setSegments(next);
    if (!next.some((segment) => segment.type === 'year')) setResetYearly(false);
  };
  const move = (index: number, direction: number) => {
    const next = [...segments];
    [next[index], next[index + direction]] = [next[index + direction]!, next[index]!];
    changeSegments(next);
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.put<{ data: NumberingSettings[] }>(
        `/tenants/me/numbering/${settings.documentType}`,
        {
          template,
          resetYearly,
          startNumber: Number(startNumber),
          nextNumber: Number(nextNumber),
          timezone,
          revision: settings.revision,
          expectedNextNumber: settings.nextNumber,
        }
      );
      client.setQueryData(queryKey, result);
      notify.success(t('numbering.saved'));
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? t('numbering.conflict')
          : t('numbering.saveError')
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{t(`numbering.types.${settings.documentType}`)}</Typography>
        {!settings.enabled && <Alert severity="info">{t('numbering.legacy')}</Alert>}
        <TextField
          select
          label={t('numbering.preset')}
          value=""
          onChange={(e) => {
            changeSegments(splitNumberingModel(e.target.value));
          }}
        >
          {[
            '{SEQ:6}',
            `${prefix}-{SEQ:6}`,
            `${prefix}{SEQ:5}`,
            `${prefix}-{YYYY}-{SEQ:4}`,
            settings.template,
          ]
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((model) => (
              <MenuItem key={model} value={model}>
                {previewNumber(model, year, 1000)}
              </MenuItem>
            ))}
        </TextField>
        <Box
          role="status"
          aria-label={t('numbering.example')}
          sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2.5 }}
        >
          <Typography variant="overline" color="text.secondary">
            {t('numbering.example')}
          </Typography>
          <Typography
            variant="h4"
            component="p"
            sx={{ fontFamily: 'monospace', fontWeight: 600, overflowWrap: 'anywhere' }}
          >
            {previews[0] ?? '—'}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t('numbering.structureHelp')}
        </Typography>
        {segments.map((segment, index) => (
          <Stack
            key={index}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems="center"
          >
            {segment.type === 'text' ? (
              <TextField
                fullWidth
                label={t('numbering.text')}
                value={segment.value}
                onChange={(e) =>
                  changeSegments(
                    segments.map((s, i) => (i === index ? { ...s, value: e.target.value } : s))
                  )
                }
              />
            ) : segment.type === 'sequence' ? (
              <TextField
                fullWidth
                type="number"
                label={t('numbering.width')}
                value={segment.value}
                slotProps={{ htmlInput: { min: 1, max: 12 } }}
                onChange={(e) =>
                  changeSegments(
                    segments.map((s, i) => (i === index ? { ...s, value: e.target.value } : s))
                  )
                }
              />
            ) : (
              <Typography sx={{ flex: 1 }}>
                {t('numbering.year')} ({year})
              </Typography>
            )}
            <Button
              aria-label={t('numbering.moveUp')}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              ↑
            </Button>
            <Button
              aria-label={t('numbering.moveDown')}
              disabled={index === segments.length - 1}
              onClick={() => move(index, 1)}
            >
              ↓
            </Button>
            <Button
              disabled={segment.type === 'sequence'}
              onClick={() => changeSegments(segments.filter((_, i) => i !== index))}
            >
              {t('numbering.remove')}
            </Button>
          </Stack>
        ))}
        <Stack direction="row" spacing={1}>
          <Button onClick={() => changeSegments([...segments, { type: 'text', value: '-' }])}>
            {t('numbering.addText')}
          </Button>
          <Button
            disabled={segments.some((s) => s.type === 'year')}
            onClick={() => changeSegments([...segments, { type: 'year', value: '' }])}
          >
            {t('numbering.addYear')}
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <SequenceNumberField
            label={t('numbering.next')}
            value={nextNumber}
            width={displayWidth}
            onChange={setNextNumber}
          />
          {hasYear && resetYearly && (
            <SequenceNumberField
              label={t('numbering.start')}
              helperText={t('numbering.startHelp')}
              value={startNumber}
              width={displayWidth}
              onChange={setStartNumber}
            />
          )}
        </Stack>
        {hasYear && (
          <FormControlLabel
            control={
              <Checkbox
                checked={resetYearly}
                onChange={(e) => {
                  setResetYearly(e.target.checked);
                }}
              />
            }
            label={t('numbering.resetYearly')}
          />
        )}
        <TextField
          select
          label={t('timezone.label')}
          value={timezone}
          helperText={t('numbering.yearHelp')}
          onChange={(e) => {
            setTimezone(e.target.value);
          }}
        >
          {timezoneSelectOptions(timezone).map((zone) => (
            <MenuItem key={zone} value={zone}>
              {zone}
            </MenuItem>
          ))}
        </TextField>
        {valid ? (
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Typography variant="body2">
              {t('numbering.preview')}: {previews.filter(Boolean).join(' → ')}
            </Typography>
            <Typography variant="body2">
              {t('numbering.nextYear', { year: year + 1 })}: {nextYear}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('numbering.previewHelp')}
            </Typography>
          </Box>
        ) : (
          <Alert severity="warning">{t('numbering.invalid')}</Alert>
        )}
        {error && (
          <Alert
            severity="error"
            action={
              <Button onClick={() => void client.invalidateQueries({ queryKey })}>
                {t('numbering.reload')}
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        <Button variant="contained" disabled={!valid || saving} onClick={() => void save()}>
          {saving ? t('saving') : t('saveChanges')}
        </Button>
      </Stack>
    </Paper>
  );
}

export function DocumentNumberingSettings() {
  const { t } = useTranslation('settings');
  const [kind, setKind] = useState('invoice');
  const query = useQuery({
    queryKey,
    queryFn: () => api.get<{ data: NumberingSettings[] }>('/tenants/me/numbering'),
    refetchOnWindowFocus: false,
  });
  if (query.isLoading) return <CircularProgress />;
  if (query.isError)
    return (
      <Alert
        severity="error"
        action={<Button onClick={() => void query.refetch()}>{t('numbering.reload')}</Button>}
      >
        {t('numbering.loadError')}
      </Alert>
    );
  return (
    <Stack spacing={3}>
      <Typography variant="h6">{t('numbering.title')}</Typography>
      <Typography color="text.secondary">{t('numbering.description')}</Typography>
      <TextField
        select
        label={t('numbering.documentType')}
        value={kind}
        onChange={(e) => setKind(e.target.value)}
      >
        {query.data?.data.map((settings) => (
          <MenuItem key={settings.documentType} value={settings.documentType}>
            {t(`numbering.types.${settings.documentType}`)}
          </MenuItem>
        ))}
      </TextField>
      {query.data?.data
        .filter((s) => s.documentType === kind)
        .map((settings) => (
          <NumberingEditor
            key={`${settings.documentType}-${settings.revision}-${settings.nextNumber}`}
            settings={settings}
          />
        ))}
    </Stack>
  );
}
